import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, ArrowLeft, Share2, Twitter, Linkedin, Facebook } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import ContactDialog from '@/components/ContactDialog';
import { getPostBySlug, getRelatedPosts } from '@/data/blogPosts';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  
  if (!slug) {
    return <Navigate to="/blog" replace />;
  }

  const post = getPostBySlug(slug);
  
  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = getRelatedPosts(post.id);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = `${post.title} - ${post.excerpt.slice(0, 100)}...`;

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | SparkWave AI Blog</title>
        <meta name="description" content={post.metaDescription} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:image" content={post.featuredImage} />
        <meta property="og:type" content="article" />
        <meta property="article:author" content={post.author} />
        <meta property="article:published_time" content={post.publishedAt} />
        {post.tags.map((tag, index) => (
          <meta key={index} property="article:tag" content={tag} />
        ))}
        <link rel="canonical" href={`/blog/${post.slug}`} />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-24">
          {/* Back to Blog */}
          <section className="py-6 px-6 md:px-10">
            <div className="container mx-auto max-w-4xl">
              <Link 
                to="/blog" 
                className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Blog
              </Link>
            </div>
          </section>

          {/* Article Header */}
          <article className="px-6 md:px-10">
            <div className="container mx-auto max-w-4xl">
              <header className="mb-12">
                <div className="aspect-video overflow-hidden rounded-lg mb-8">
                  <img 
                    src={post.featuredImage} 
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in leading-tight">
                  {post.title}
                </h1>
                
                <div className="flex flex-wrap items-center gap-6 text-muted-foreground mb-8 animate-fade-in delay-200 py-4 border-y border-border/50">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    <span className="font-medium">{formatDate(post.publishedAt)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    <span className="font-medium">{post.readTime} min read</span>
                  </div>
                  <span className="font-medium">By {post.author}</span>
                </div>
                
                <div className="flex flex-wrap gap-3 mb-8 animate-fade-in delay-300">
                  {post.tags.map((tag) => (
                    <span 
                      key={tag} 
                      className="px-4 py-2 bg-primary/10 text-primary text-sm font-medium rounded-full border border-primary/20 hover:bg-primary/15 transition-colors"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Social Share */}
                <div className="flex items-center gap-4 py-6 border-t border-b border-border animate-fade-in delay-400">
                  <span className="text-sm font-semibold text-foreground">Share this article:</span>
                  <div className="flex gap-3">
                    <a 
                      href={shareLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-all duration-300 hover:scale-110 shadow-md hover:shadow-lg"
                      aria-label="Share on Twitter"
                    >
                      <Twitter className="h-4 w-4" />
                    </a>
                    <a 
                      href={shareLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-full bg-blue-700 text-white hover:bg-blue-800 transition-all duration-300 hover:scale-110 shadow-md hover:shadow-lg"
                      aria-label="Share on LinkedIn"
                    >
                      <Linkedin className="h-4 w-4" />
                    </a>
                    <a 
                      href={shareLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-all duration-300 hover:scale-110 shadow-md hover:shadow-lg"
                      aria-label="Share on Facebook"
                    >
                      <Facebook className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </header>

              {/* Article Content */}
              <div 
                className="prose prose-lg max-w-none mb-16 animate-fade-in delay-500"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Contact CTA */}
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-8 mb-12 text-center animate-fade-in delay-600">
                <h3 className="text-2xl font-bold mb-4">
                  Ready to Transform Your Business with AI?
                </h3>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Get expert guidance on implementing AI solutions that drive real business results. 
                  Book a free discovery call to discuss your specific needs.
                </p>
                <ContactDialog 
                  trigger={
                    <Button size="lg" className="btn-hover-effect">
                      Book Your Free Consultation
                    </Button>
                  }
                />
              </div>
            </div>
          </article>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <section className="py-16 px-6 md:px-10 bg-muted/30">
              <div className="container mx-auto max-w-6xl">
                <h2 className="text-3xl font-bold mb-8 text-center">Related Posts</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {relatedPosts.map((relatedPost) => (
                    <Card 
                      key={relatedPost.id} 
                      className="overflow-hidden border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img 
                          src={relatedPost.featuredImage} 
                          alt={relatedPost.title}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                      </div>
                      
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                          <Calendar className="h-3 w-3" />
                          {formatDate(relatedPost.publishedAt)}
                        </div>
                        
                        <h3 className="text-lg font-bold mb-2 line-clamp-2">
                          <Link 
                            to={`/blog/${relatedPost.slug}`}
                            className="hover:text-primary transition-colors"
                          >
                            {relatedPost.title}
                          </Link>
                        </h3>
                        
                        <p className="text-muted-foreground text-sm line-clamp-3">
                          {relatedPost.excerpt}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default BlogPost;